import { useRef, useState } from 'react';
import { Plus, Copy, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import ConfirmDialog from '../ConfirmDialog';
import MiniPagePreview from '../story-dashboard/MiniPagePreview';

export default function PageStrip() {
  const story = useEditorStore((s) => s.story);
  const activePageId = useEditorStore((s) => s.activePageId);
  const setActivePage = useEditorStore((s) => s.setActivePage);
  const addPage = useEditorStore((s) => s.addPage);
  const deletePage = useEditorStore((s) => s.deletePage);
  const duplicatePage = useEditorStore((s) => s.duplicatePage);
  const reorderPages = useEditorStore((s) => s.reorderPages);

  const stripRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [confirmPageId, setConfirmPageId] = useState(null);

  if (!story) return null;

  const pages = [...story.pages].sort((a, b) => a.pageIndex - b.pageIndex);

  function handleDragStart(index) {
    dragItem.current = index;
  }

  function handleDragEnter(index) {
    dragOverItem.current = index;
  }

  function handleDragEnd() {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current !== dragOverItem.current) {
      reorderPages(dragItem.current, dragOverItem.current);
    }
    dragItem.current = null;
    dragOverItem.current = null;
  }

  function handleDeletePage(pageId, e) {
    e.stopPropagation();
    if (pages.length === 1) return;
    setConfirmPageId(pageId);
  }

  function handleDuplicatePage(pageId, e) {
    e.stopPropagation();
    duplicatePage(pageId);
  }

  function getActiveIndex() {
    return pages.findIndex((p) => p.id === activePageId);
  }

  function scrollTo(direction) {
    if (!stripRef.current) return;
    stripRef.current.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  }

  return (
    <div className="h-24 flex items-center flex-shrink-0"
         style={{ background: 'var(--t-surface)', borderTop: '1px solid var(--t-border)' }}>
      {/* Scroll left */}
      <button
        onClick={() => scrollTo('left')}
        className="flex-shrink-0 h-full px-2 transition-colors icon-btn rounded-none"
      >
        <ChevronLeft size={14} />
      </button>

      {/* Pages scroll area */}
      <div
        ref={stripRef}
        className="flex-1 flex items-center gap-2 overflow-x-auto py-2 px-1 scrollbar-hide"
        style={{ scrollbarWidth: 'none' }}
      >
        {pages.map((page, index) => (
          <PageThumbnail
            key={page.id}
            page={page}
            index={index}
            isActive={page.id === activePageId}
            onClick={() => setActivePage(page.id)}
            onDelete={(e) => handleDeletePage(page.id, e)}
            onDuplicate={(e) => handleDuplicatePage(page.id, e)}
            onDragStart={() => handleDragStart(index)}
            onDragEnter={() => handleDragEnter(index)}
            onDragEnd={handleDragEnd}
          />
        ))}

        {/* Add page button */}
        <button
          onClick={() => addPage(getActiveIndex())}
          className="flex-shrink-0 w-12 aspect-[9/16] rounded-lg border-2 border-dashed
                     flex items-center justify-center transition-all"
          style={{ borderColor: 'var(--t-border2)', color: 'var(--t-muted)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.color = 'var(--brand-primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--t-border2)'; e.currentTarget.style.color = 'var(--t-muted)'; }}
          title="Add page"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Scroll right */}
      <button
        onClick={() => scrollTo('right')}
        className="flex-shrink-0 h-full px-2 transition-colors icon-btn rounded-none"
      >
        <ChevronRight size={14} />
      </button>

      {/* Page count */}
      <div className="flex-shrink-0 px-3 text-xs h-full flex items-center"
           style={{ borderLeft: '1px solid var(--t-border)', color: 'var(--t-muted)' }}>
        <div>
          <div className="font-medium" style={{ color: 'var(--t-text2)' }}>{getActiveIndex() + 1}/{pages.length}</div>
          <div>pages</div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmPageId !== null}
        onClose={() => setConfirmPageId(null)}
        onConfirm={() => deletePage(confirmPageId)}
        title="Delete page?"
        message="This page will be permanently removed from the story."
        confirmLabel="Delete"
      />
    </div>
  );
}

function PageThumbnail({ page, index, isActive, onClick, onDelete, onDuplicate, onDragStart, onDragEnter, onDragEnd }) {
  return (
    <div
      className={`page-thumbnail flex-shrink-0 group relative cursor-grab active:cursor-grabbing
        ${isActive ? 'active ring-2 ring-indigo-500' : 'hover:ring-1 hover:ring-indigo-400/40'}`}
      style={{ width: '48px', aspectRatio: '9/16' }}
      onClick={onClick}
      draggable
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
    >
      {/* Mini canvas — full fidelity preview */}
      <div className="w-full h-full overflow-hidden rounded-lg relative bg-neutral-900">
        <MiniPagePreview background={page.background} layers={page.layers} />

        {/* Page number */}
        <div className="absolute bottom-0.5 left-0 right-0 flex justify-center z-[2]">
          <span className="text-white text-[8px] font-medium bg-black/50 px-1 rounded">
            {index + 1}
          </span>
        </div>
      </div>

      {/* Hover actions */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity
                      flex items-center justify-center gap-1 z-10 bg-black/30 rounded-lg">
        <button
          onClick={onDuplicate}
          className="w-5 h-5 rounded flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          title="Duplicate"
        >
          <Copy size={9} className="text-white" />
        </button>
        <button
          onClick={onDelete}
          className="w-5 h-5 bg-red-900/90 rounded flex items-center justify-center hover:bg-red-800"
          title="Delete"
        >
          <Trash2 size={9} className="text-red-400" />
        </button>
      </div>
    </div>
  );
}
